import { AlertCircle } from 'lucide-react';
import { useEffect } from 'react';
import { useChatbot } from '../../../contexts/ChatbotContext';
import { useMiniApps } from '../../../hooks/useMiniApps';

export function MiniAppColorPicker() {
  const { chatbotPublicId } = useChatbot();
  const { buttonColors, setButtonColors, loading, error, initializeMiniApps } = useMiniApps();

  const handleColorChange = (key: keyof typeof buttonColors, value: string) => {
    if (chatbotPublicId) {
      setButtonColors({ [key]: value }, chatbotPublicId);
    }
  };

  // Initialize miniApps when component mounts
  useEffect(() => {
    if (chatbotPublicId) {
      initializeMiniApps(chatbotPublicId);
    }
  }, [chatbotPublicId, initializeMiniApps]);

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-500/10 text-red-400 p-4 rounded-lg flex items-center gap-2 mb-4">
          <AlertCircle className="w-5 h-5" />
          <p>{error.message}</p>
        </div>
      )}
      
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Start color
        </label>
        <div className="flex items-center gap-3">
          <div className="relative">
            <input
              type="color"
              value={buttonColors.startColor}
              onChange={(e) => handleColorChange('startColor', e.target.value)}
              className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
              disabled={loading}
            />
            <div 
              className="w-10 h-10 rounded border border-white/10"
              style={{ backgroundColor: buttonColors.startColor }}
            />
          </div>
          <input
            type="text"
            value={buttonColors.startColor}
            onChange={(e) => handleColorChange('startColor', e.target.value)}
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            disabled={loading}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          End color
        </label>
        <div className="flex items-center gap-3">
          <div className="relative">
            <input
              type="color"
              value={buttonColors.endColor}
              onChange={(e) => handleColorChange('endColor', e.target.value)}
              className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
              disabled={loading}
            />
            <div 
              className="w-10 h-10 rounded border border-white/10"
              style={{ backgroundColor: buttonColors.endColor }}
            />
          </div>
          <input
            type="text"
            value={buttonColors.endColor}
            onChange={(e) => handleColorChange('endColor', e.target.value)}
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            disabled={loading}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Text color
        </label>
        <div className="flex items-center gap-3">
          <div className="relative">
            <input
              type="color"
              value={buttonColors.textColor}
              onChange={(e) => handleColorChange('textColor', e.target.value)}
              className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
              disabled={loading}
            />
            <div 
              className="w-10 h-10 rounded border border-white/10"
              style={{ backgroundColor: buttonColors.textColor }}
            />
          </div>
          <input
            type="text"
            value={buttonColors.textColor}
            onChange={(e) => handleColorChange('textColor', e.target.value)}
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            disabled={loading}
          />
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center p-2">
          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-indigo-500"></div>
          <span className="ml-2 text-xs text-gray-400">Saving in progress...</span>
        </div>
      )}
    </div>
  );
}